import React, { useEffect, useState } from 'react';
import classes from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { useKato } from '../../../contexts/kato.context';
import { useShared } from '../../../contexts/shared.context';
import {
  GeneralInfoStatus,
  IBasicStats,
  KatoType,
} from '../../../types/kato.types';
import { toast } from 'react-toastify';
import { updateGeneralInfoData } from '../../../requests/supervisor.request';
import { getIncreaseVal, isEmpty, t } from '../../../helpers/utils';
import KatoSelects from '../components/kato-selects.component';
import FooterComponent from '../components/footer.component';
import Field from '../../../components/field/field.component';

const baseClass = 'ser-tab';
const translationKey = 'admin.ser';

const types: Array<IBasicStats['type']> = [
  'SER',
  'BUDGET',
  'MCB',
  'EMPLOYMENT',
  'LIVESTOCK',
];

const SerTab = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const { katoGeneralInfo, updatedGeneralInfo, currentLevel } = useKato();
  const { basicStats, status } = katoGeneralInfo || {};
  const { selectedInfoYear } = useShared();

  const [formData, setFormData] = useState({
    basicStats: basicStats || [],
    status: status || GeneralInfoStatus.IN_PROCESS,
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const fields: Partial<
    Record<
      IBasicStats['type'],
      Array<keyof Omit<IBasicStats, 'additionalInfo'>>
    >
  > = {
    SER:
      currentLevel === KatoType.OBLAST
        ? [
            'previousYearVal',
            'currentYearVal',
            'increaseVal',
            'ifo',
            'ifoPlace',
          ]
        : ['previousYearVal', 'currentYearVal', 'increaseVal', 'ifo'],
    BUDGET: ['previousYearVal', 'currentYearVal', 'increaseVal'],
    MCB: ['previousYearVal', 'currentYearVal', 'increaseVal', 'ifo'],
    EMPLOYMENT: ['previousYearVal', 'currentYearVal', 'increaseVal', 'ifo'],
    LIVESTOCK: ['previousYearVal', 'currentYearVal', 'increaseVal', 'ifo'],
  };

  const additionalFields: Partial<Record<IBasicStats['type'], string[]>> = {
    SER: currentLevel === KatoType.OBLAST ? ['place'] : [],
    BUDGET: ['development'],
  };

  const handleSave = () => {
    if (validateFields()) {
      const data = {
        basicStats: formData.basicStats,
        status: formData.status,
      };
      updateGeneralInfoData(katoGeneralInfo.kato, data, selectedInfoYear).then(
        () => {
          updatedGeneralInfo(selectedInfoYear);
          toast.success(t(`admin.success`, language));
        },
      );
    }
  };

  const validateFields = () => {
    const newErrors: Record<string, boolean> = {};

    const fieldMap: Partial<
      Record<
        IBasicStats['type'],
        Array<
          keyof Pick<IBasicStats, 'currentYearVal' | 'previousYearVal' | 'ifo'>
        >
      >
    > = {
      SER: ['currentYearVal', 'previousYearVal', 'ifo'],
      BUDGET: ['currentYearVal', 'previousYearVal'],
      MCB: ['currentYearVal', 'previousYearVal', 'ifo'],
      EMPLOYMENT: ['currentYearVal', 'previousYearVal', 'ifo'],
      LIVESTOCK: ['currentYearVal', 'previousYearVal', 'ifo'],
    };

    (
      Object.entries(fieldMap) as [
        IBasicStats['type'],
        Array<keyof IBasicStats>,
      ][]
    ).forEach(([type, requiredFields]) => {
      const items = formData.basicStats.filter((item) => item.type === type);

      for (const item of items) {
        const filledCount = requiredFields.filter(
          (field) => !isEmpty(item[field]),
        ).length;

        if (filledCount > 0 && filledCount < requiredFields.length) {
          newErrors[`${type}-${item.subType}`] = true;
        }
      }
    });

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  useEffect(() => {
    setFormData({
      basicStats: basicStats || [],
      status: status || GeneralInfoStatus.IN_PROCESS,
    });
  }, [basicStats, katoGeneralInfo.kato, selectedInfoYear]);

  const renderFields = (lang: 'ru' | 'kz') => {
    const updateField = (
      type: IBasicStats['type'],
      subType: string,
      field: keyof IBasicStats,
      value: string | number,
    ) => {
      setFormData((prev) => ({
        ...prev,
        basicStats: prev.basicStats.map((stat) =>
          stat.type === type && stat.subType === subType
            ? {
                ...stat,
                [field]: value === '' ? null : +value, // <-- допускаем очистку
              }
            : stat,
        ),
      }));
    };

    const updateAdditionalField = (
      type: IBasicStats['type'],
      subType: string,
      field: string,
      value: string | number,
    ) => {
      setFormData((prev) => ({
        ...prev,
        basicStats: prev.basicStats.map((stat) =>
          stat.type === type && stat.subType === subType
            ? {
                ...stat,
                additionalInfo: {
                  ...stat.additionalInfo,
                  [field]: value,
                },
              }
            : stat,
        ),
      }));
    };

    return (
      <div className={classes['fields-container']}>
        {types.map((type) => {
          const stats = formData.basicStats.filter(
            (stat) => stat.type === type,
          );
          return (
            <div key={type} className={classes['field-block']}>
              <div className={classes['field-block__title']}>
                {t(`${translationKey}.${type}.title`, lang)}
              </div>

              {stats.map((stat) => (
                <div key={stat.subType} className={classes['field-block']}>
                  <div className={classes['field-block__title']}>
                    {t(`${translationKey}.${type}.${stat.subType}.title`, lang)}
                  </div>

                  {fields[type]?.map((field) => (
                    <Field
                      key={`stat.${stat.subType}.${field}`}
                      value={
                        field === 'increaseVal'
                          ? getIncreaseVal(stat, formData.basicStats)
                          : (stat[field] ?? '')
                      }
                      label={t(
                        `${translationKey}.${type}.${stat.subType}.${field}`,
                        lang,
                        {
                          year:
                            field === 'currentYearVal'
                              ? selectedInfoYear
                              : selectedInfoYear - 1,
                          unit:
                            currentLevel === KatoType.OBLAST
                              ? t('billion', lang)
                              : t('million', lang),
                          region:
                            currentLevel === KatoType.OBLAST
                              ? t(`${translationKey}.region`, lang)
                              : t(`${translationKey}.district`, lang),
                        },
                      )}
                      onChange={(val) =>
                        updateField(type, stat.subType, field, val)
                      }
                      disabled={field === 'increaseVal'}
                      required
                      type="number"
                      error={errors[`${type}-${stat.subType}`]}
                    />
                  ))}

                  {additionalFields[type]?.map((field) => (
                    <Field
                      key={`stat.${stat.subType}.${field}`}
                      value={stat.additionalInfo?.[field] ?? ''}
                      label={t(
                        `${translationKey}.${type}.${stat.subType}.${field}`,
                        lang,
                        {
                          unit:
                            currentLevel === KatoType.OBLAST
                              ? t('billion', lang)
                              : t('million', lang),
                        },
                      )}
                      onChange={(val) =>
                        updateAdditionalField(type, stat.subType, field, +val)
                      }
                      required
                    />
                  ))}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  if (!basicStats || !katoGeneralInfo) return null;
  return (
    <div className={classes[baseClass]}>
      <div className={classes[`grid`]}>
        <KatoSelects lang={language as 'ru' | 'kz'} />
        <KatoSelects lang={language === 'ru' ? 'kz' : 'ru'} />
        {renderFields(language as 'ru' | 'kz')}
        {renderFields(language === 'ru' ? 'kz' : 'ru')}
      </div>

      <FooterComponent
        onSave={handleSave}
        status={formData.status}
        validateFields={validateFields}
      />
    </div>
  );
};

export default SerTab;
